import { z } from 'zod';
/**
 * Motor-designer contract — the single source of truth for the design request and response, shared by
 * the server (runtime validation + computation) and the web screen (form types + rendering). The
 * request carries only what a person actually specifies about the duty; Kia's frozen tooling geometry,
 * material models and thermal network are filled server-side from the default spec. Every field has a
 * sensible default so the endpoint is forgiving — the user is not expected to get the request perfect.
 *
 * Amounts are Rial. Prices are OPTIONAL: if omitted, the server uses a clearly-flagged placeholder book
 * and marks `pricesArePlaceholder` in the response — it never silently invents a cost as if it were real.
 */
export const MOTOR_APPLICATIONS = ['desert_cooler', 'range_hood', 'other'];
export const MOTOR_APPLICATION_LABELS_FA = {
    desert_cooler: 'موتور کولر آبی',
    range_hood: 'موتور هود',
    other: 'سایر',
};
/** Optional price overrides, matching Kia's BOM_COD groups (ریال/kg مؤثر and ریال/piece). */
export const MotorPriceBookSchema = z.object({
    aluminiumWirePerKg: z.number().nonnegative(),
    aluminiumCastPerKg: z.number().nonnegative(),
    electricalSteelPerKg: z.number().nonnegative(),
    shaftSteelPerKg: z.number().nonnegative(),
    capacitorPerPiece: z.number().nonnegative(),
    bearingPerPiece: z.number().nonnegative(),
    // NOTE: labour+overhead is NOT a client price — it is absorbed server-side at the month's conversion
    // rate (winding-Kg × rial/Kg), the SAME rate every product pays. See routes/motor.ts designPolicy.
});
/**
 * Landed-cost components for ONE material (per kg or per piece), entered by the costing office. The
 * server builds up base → CIF → duty → levy → clearance → financing → landed. Import terms default to
 * zero, so a domestic material is just base + clearance (+ financing).
 */
export const LandedComponentsSchema = z.object({
    basePerUnit: z.number().nonnegative('قیمت پایه نمی‌تواند منفی باشد'),
    freightInsurancePct: z.number().min(0).max(3).default(0),
    customsDutyPct: z.number().min(0).max(3).default(0),
    importLevyPct: z.number().min(0).max(3).default(0),
    clearanceHandlingPerUnit: z.number().min(0).default(0),
    financingPct: z.number().min(0).max(3).default(0),
});
/**
 * Frozen-lamination tooling dimensions the TECHNICAL OFFICE reads off the die drawing — in MILLIMETRES,
 * the way an engineer thinks. All optional: whatever is given overrides the default lamination, the rest
 * keeps its default. These drive the real Carter factor, tooth/yoke flux (saturation), and slot fill.
 */
export const MotorLaminationSchema = z
    .object({
    boreDiameterMm: z.number().positive().max(500),
    outerDiameterMm: z.number().positive().max(600),
    slots: z.number().int().positive().max(96),
    toothWidthMm: z.number().positive().max(50),
    yokeDepthMm: z.number().positive().max(50),
    airgapMm: z.number().positive().max(5),
    slotWidthMm: z.number().positive().max(50),
    slotHeightMm: z.number().positive().max(100),
})
    .partial();
/**
 * Calibration input — a motor Kia ALREADY makes, with its MEASURED performance. The server fits the
 * model's constants (core loss, thermal) so it reproduces this motor, then applies that calibration to
 * every future design. This is the bridge from a representative model to one production can trust.
 */
export const CalibrationInputSchema = z.object({
    /** The known motor's main-winding series turns. */
    seriesTurns: z.number().int().positive().max(5000),
    supplyVoltage: z.number().positive().max(400).default(220),
    poles: z.number().int().positive().max(24).default(4),
    /** Operating slip at which the readings were taken. */
    slip: z.number().positive().max(1),
    /** The motor's run capacitor [µF]. */
    capacitorUF: z.number().positive().max(200),
    /** MEASURED line current [A]. */
    lineCurrentA: z.number().positive().max(50),
    /** MEASURED shaft power [W] (dyno). */
    shaftPowerW: z.number().positive().max(2000),
    /** MEASURED winding temperature rise above ambient [K]. */
    tempRiseK: z.number().positive().max(200),
});
/** The design request — what the user (or the AI orchestration) specifies about the duty. */
export const MotorDesignRequestSchema = z.object({
    application: z.enum(MOTOR_APPLICATIONS).default('desert_cooler'),
    /** Required shaft output power at the duty point [W]. */
    requiredShaftPowerW: z
        .number()
        .positive('توان خروجی باید بزرگ‌تر از صفر باشد')
        .max(2000, 'خارج از دامنهٔ موتورهای کسری‌اسب'),
    /** Nominal duty speed [rpm]. */
    targetSpeedRpm: z.number().positive('دور نامی نامعتبر است').max(6000).default(1380),
    /** Supply. */
    supplyVoltage: z.number().positive().max(400).default(220),
    frequency: z.number().positive().max(400).default(50),
    poles: z.number().int().positive().max(24).default(4),
    /** Minimum line voltage the motor must still start at [V]. */
    minStartVoltage: z.number().positive().max(400).default(190),
    /** Minimum starting torque required at min voltage [N·m]. */
    requiredStartTorqueNm: z.number().nonnegative().max(50).default(0.1),
    /** Worst-case ambient [°C]. */
    ambientC: z.number().min(-20).max(120).default(45),
    /** Required thermal safety margin below the insulation-class limit [K]. */
    insulationMarginK: z.number().min(0).max(80).default(10),
    /** Sale price for the margin gate [Rial]. */
    salePriceRial: z.number().positive('قیمت فروش باید بزرگ‌تر از صفر باشد'),
    /** Optional current price book; when omitted a flagged placeholder is used. */
    prices: MotorPriceBookSchema.optional(),
    /** Optional frozen-lamination geometry (technical office); overrides the default lamination. */
    lamination: MotorLaminationSchema.optional(),
});
//# sourceMappingURL=motor.js.map